import React, { useMemo, forwardRef } from "react";
import ReactMarkdown from "react-markdown";
import katex from "katex";
import "katex/dist/katex.min.css";
import DOMPurify from "dompurify";
import { BiologySVG } from "@/components/chat/BiologySVG";
import NotionIllustration from "@/components/chat/NotionIllustration";
import { SvgMultilineText, getContrastTextColor, splitLabel, withAlpha } from "@/components/chat/visual-utils";

const VISUAL_BLOCK_TYPES = ["plot", "geometry", "diagram", "map", "biology", "svg"] as const;

// Parse [ILLUSTRATION_PHOTO: description | subject | topic] markers from text.
type PhotoMarker = { kind: "photo"; description: string; subject?: string; topic?: string };
type TextSeg = { kind: "text"; data: string };
function splitOnPhotoMarkers(text: string): Array<TextSeg | PhotoMarker> {
  const re = /\[ILLUSTRATION_PHOTO:\s*([^\]]+?)\]/g;
  const out: Array<TextSeg | PhotoMarker> = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ kind: "text", data: text.slice(last, m.index) });
    const parts = m[1].split("|").map((s) => s.trim()).filter(Boolean);
    out.push({ kind: "photo", description: parts[0] || "", subject: parts[1], topic: parts[2] });
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ kind: "text", data: text.slice(last) });
  return out.length ? out : [{ kind: "text", data: text }];
}

// Hide an incomplete `[ILLUSTRATION_PHOTO:` marker still being streamed
function hideIncompletePhotoMarker(content: string): string {
  const idx = content.lastIndexOf("[ILLUSTRATION_PHOTO");
  if (idx === -1) return content;
  // If a closing ] exists after this opening, it's complete
  const closing = content.indexOf("]", idx);
  if (closing !== -1) return content;
  return content.slice(0, idx).trimEnd();
}

function maskHttpSources(content: string) {
  return content
    .replace(/<kelasai_meta>[\s\S]*?<\/kelasai_meta>/gi, "")
    .replace(/<kelasai_meta>[\s\S]*$/gi, "")
    .replace(/https?:\/\/[^\s)\]]*\/storage\/v1\/object\/public\/avatars\/[^\s)\]]+/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function hideIncompleteVisualBlock(content: string) {
  let cursor = 0;

  while (cursor < content.length) {
    let nextMatchIndex = -1;
    let openerLength = 0;

    VISUAL_BLOCK_TYPES.forEach((type) => {
      const token = `\`\`\`${type}`;
      const index = content.indexOf(token, cursor);
      if (index !== -1 && (nextMatchIndex === -1 || index < nextMatchIndex)) {
        nextMatchIndex = index;
        openerLength = token.length;
      }
    });

    if (nextMatchIndex === -1) return content;

    const closingFenceIndex = content.indexOf("```", nextMatchIndex + openerLength);
    if (closingFenceIndex === -1) {
      return content.slice(0, nextMatchIndex).trimEnd();
    }

    cursor = closingFenceIndex + 3;
  }

  return content;
}

function renderLatexHtml(tex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(tex.trim(), { displayMode, throwOnError: false });
  } catch {
    return `<code>${tex}</code>`;
  }
}

export function renderLatexInline(node: React.ReactNode, keyPrefix = "latex"): React.ReactNode {
  return renderLatexNodes(node, keyPrefix);
}

function renderLatexNodes(node: React.ReactNode, keyPrefix = "latex"): React.ReactNode {
  if (typeof node === "string") {
    const pattern = /(\$\$[\s\S]*?\$\$|\$[^$\n]+?\$)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(node)) !== null) {
      if (match.index > lastIndex) {
        parts.push(node.slice(lastIndex, match.index));
      }

      const token = match[0];
      const displayMode = token.startsWith("$$") && token.endsWith("$$");
      const tex = token.slice(displayMode ? 2 : 1, displayMode ? -2 : -1);

      parts.push(
        displayMode ? (
          <div
            key={`${keyPrefix}-${match.index}`}
            className="my-3 overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: renderLatexHtml(tex, true) }}
          />
        ) : (
          <span
            key={`${keyPrefix}-${match.index}`}
            className="inline-block align-middle"
            dangerouslySetInnerHTML={{ __html: renderLatexHtml(tex, false) }}
          />
        )
      );

      lastIndex = pattern.lastIndex;
    }

    if (lastIndex < node.length) {
      parts.push(node.slice(lastIndex));
    }

    return parts.length > 0 ? parts : node;
  }

  if (Array.isArray(node)) {
    return node.map((child, index) => renderLatexNodes(child, `${keyPrefix}-${index}`));
  }

  if (React.isValidElement<{ children?: React.ReactNode }>(node) && node.props.children) {
    return React.cloneElement(node, {
      ...node.props,
      children: renderLatexNodes(node.props.children, keyPrefix),
    });
  }

  return node;
}

// SVG Plot renderer
const PlotSVG = forwardRef<HTMLDivElement, { data: any }>(({ data }, ref) => {
  const { functions: fns = [], xMin = -5, xMax = 5, yMin = -5, yMax = 10, title = "" } = data;
  const w = 400, h = 300, pad = 40;
  const colors = ["#6C5CE7", "#E74C3C", "#2ECC71", "#F39C12", "#3498DB"];

  const toSVGX = (x: number) => pad + ((x - xMin) / (xMax - xMin)) * (w - 2 * pad);
  const toSVGY = (y: number) => h - pad - ((y - yMin) / (yMax - yMin)) * (h - 2 * pad);

  const evalFn = (expr: string, x: number): number => {
    try {
      const sanitized = expr.replace(/\^/g, "**").replace(/sin/g, "Math.sin").replace(/cos/g, "Math.cos").replace(/tan/g, "Math.tan").replace(/sqrt/g, "Math.sqrt").replace(/abs/g, "Math.abs").replace(/log/g, "Math.log").replace(/pi/g, "Math.PI").replace(/exp/g, "Math.exp");
      return new Function("x", `return ${sanitized}`)(x) as number;
    } catch { return NaN; }
  };

  const paths = fns.map((fn: string, fi: number) => {
    const pts: string[] = [];
    for (let i = 0; i <= 200; i++) {
      const x = xMin + (i / 200) * (xMax - xMin);
      const y = evalFn(fn, x);
      if (isFinite(y) && y >= yMin - 10 && y <= yMax + 10) {
        pts.push(`${toSVGX(x).toFixed(1)},${toSVGY(Math.max(yMin, Math.min(yMax, y))).toFixed(1)}`);
      } else if (pts.length > 0) { pts.push("M"); }
    }
    let d = ""; let first = true;
    for (const pt of pts) {
      if (pt === "M") { first = true; continue; }
      d += first ? `M${pt}` : `L${pt}`; first = false;
    }
    return <g key={fi}><path d={d} fill="none" stroke={colors[fi % colors.length]} strokeWidth="2.5" strokeLinecap="round" /><text x={w - pad + 5} y={toSVGY(evalFn(fn, xMax))} fill={colors[fi % colors.length]} fontSize="10" fontWeight="bold" dominantBaseline="middle">{fn}</text></g>;
  });

  const gridLines: JSX.Element[] = [];
  for (let x = Math.ceil(xMin); x <= Math.floor(xMax); x++) {
    const sx = toSVGX(x);
    gridLines.push(<line key={`gx${x}`} x1={sx} y1={pad} x2={sx} y2={h - pad} stroke="hsl(var(--border))" strokeWidth="0.5" />);
    gridLines.push(<text key={`tx${x}`} x={sx} y={h - pad + 15} textAnchor="middle" fontSize="9" fill="hsl(var(--muted-foreground))">{x}</text>);
  }
  for (let y = Math.ceil(yMin); y <= Math.floor(yMax); y++) {
    const sy = toSVGY(y);
    gridLines.push(<line key={`gy${y}`} x1={pad} y1={sy} x2={w - pad} y2={sy} stroke="hsl(var(--border))" strokeWidth="0.5" />);
    gridLines.push(<text key={`ty${y}`} x={pad - 5} y={sy + 3} textAnchor="end" fontSize="9" fill="hsl(var(--muted-foreground))">{y}</text>);
  }

  return (
    <div ref={ref} className="my-3 rounded-2xl bg-card border border-border p-3 overflow-x-auto">
      {title && <p className="text-xs font-bold text-foreground mb-2 text-center">{title}</p>}
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full max-w-md mx-auto" style={{ maxHeight: 300 }}>
        {gridLines}
        <line x1={toSVGX(0)} y1={pad} x2={toSVGX(0)} y2={h - pad} stroke="hsl(var(--foreground))" strokeWidth="1" opacity="0.3" />
        <line x1={pad} y1={toSVGY(0)} x2={w - pad} y2={toSVGY(0)} stroke="hsl(var(--foreground))" strokeWidth="1" opacity="0.3" />
        {paths}
      </svg>
    </div>
  );
});
PlotSVG.displayName = "PlotSVG";

// Geometry renderer
const GeometrySVG = forwardRef<HTMLDivElement, { data: any }>(({ data }, ref) => {
  const { shapes = [], width = 400, height = 400, title = "" } = data;
  const renderShape = (s: any, i: number) => {
    switch (s.type) {
      case "circle": {
        const color = s.color || "#6C5CE7";
        return <g key={i}><circle cx={s.cx} cy={s.cy} r={s.r} fill={withAlpha(color, 0.18)} stroke={color} strokeWidth="2" />{s.label && <SvgMultilineText x={s.cx} y={s.cy} label={s.label} fill={getContrastTextColor(color)} fontSize={Math.max(10, Math.min(13, s.r / 4))} maxCharsPerLine={Math.max(8, Math.round((s.r * 2) / 10))} />}</g>;
      }
      case "line": return <g key={i}><line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={s.color || "#E74C3C"} strokeWidth="2" />{s.label && <text x={(s.x1 + s.x2) / 2} y={(s.y1 + s.y2) / 2 - 8} textAnchor="middle" fontSize="10" fill={s.color || "#E74C3C"}>{s.label}</text>}</g>;
      case "triangle": {
        if (!s.points?.length || s.points.length < 3) return null;
        const color = s.color || "#2ECC71";
        const centerX = s.points.reduce((a: number, p: number[]) => a + p[0], 0) / 3;
        const centerY = s.points.reduce((a: number, p: number[]) => a + p[1], 0) / 3;
        return <g key={i}><polygon points={s.points.map((p: number[]) => p.join(",")).join(" ")} fill={withAlpha(color, 0.16)} stroke={color} strokeWidth="2" />{s.label && <SvgMultilineText x={centerX} y={centerY} label={s.label} fill={getContrastTextColor(color)} fontSize={11} maxCharsPerLine={10} />}</g>;
      }
      case "rect": {
        const color = s.color || "#3498DB";
        return <g key={i}><rect x={s.x} y={s.y} width={s.w} height={s.h} fill={withAlpha(color, 0.18)} stroke={color} strokeWidth="2" rx="10" />{s.label && <SvgMultilineText x={s.x + s.w / 2} y={s.y + s.h / 2} label={s.label} fill={getContrastTextColor(color)} fontSize={11} maxCharsPerLine={Math.max(8, Math.round(s.w / 11))} />}</g>;
      }
      case "angle": {
        const sr = (s.startAngle * Math.PI) / 180, er = (s.endAngle * Math.PI) / 180, ar = s.r || 30;
        const x1 = s.cx + ar * Math.cos(sr), y1 = s.cy - ar * Math.sin(sr);
        const x2 = s.cx + ar * Math.cos(er), y2 = s.cy - ar * Math.sin(er);
        return <g key={i}><path d={`M ${x1} ${y1} A ${ar} ${ar} 0 ${(s.endAngle - s.startAngle) > 180 ? 1 : 0} 0 ${x2} ${y2}`} fill="none" stroke={s.color || "#F39C12"} strokeWidth="2" />{s.label && <text x={s.cx + (ar + 15) * Math.cos((sr + er) / 2)} y={s.cy - (ar + 15) * Math.sin((sr + er) / 2)} textAnchor="middle" fontSize="10" fill={s.color || "#F39C12"}>{s.label}</text>}</g>;
      }
      case "point": return <g key={i}><circle cx={s.x} cy={s.y} r={4} fill={s.color || "#E74C3C"} />{s.label && <text x={s.x + 10} y={s.y - 8} fontSize="11" fontWeight="bold" fill={s.color || "#E74C3C"}>{s.label}</text>}</g>;
      default: return null;
    }
  };
  return (
    <div ref={ref} className="my-3 rounded-2xl bg-card border border-border p-3 overflow-x-auto">
      {title && <p className="text-xs font-bold text-foreground mb-2 text-center">{title}</p>}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-sm mx-auto" style={{ maxHeight: 350 }}>{shapes.map(renderShape)}</svg>
    </div>
  );
});
GeometrySVG.displayName = "GeometrySVG";

// Diagram renderer
const DiagramSVG = forwardRef<HTMLDivElement, { data: any }>(({ data }, ref) => {
  const { nodes = [], edges = [], title = "", width = 400, height = 350 } = data;
  return (
    <div ref={ref} className="my-3 rounded-2xl bg-card border border-border p-3 overflow-x-auto">
      {title && <p className="text-xs font-bold text-foreground mb-2 text-center">{title}</p>}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-sm mx-auto" style={{ maxHeight: 350 }}>
        <defs><marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill="hsl(var(--muted-foreground))" /></marker></defs>
        {edges.map((e: any, i: number) => {
          const from = nodes.find((n: any) => n.id === e.from), to = nodes.find((n: any) => n.id === e.to);
          if (!from || !to) return null;
          return <g key={`e${i}`}><line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="hsl(var(--muted-foreground))" strokeWidth="1.5" markerEnd="url(#arrowhead)" opacity="0.6" />{e.label && <text x={(from.x + to.x) / 2} y={(from.y + to.y) / 2 - 6} textAnchor="middle" fontSize="9" fill="hsl(var(--muted-foreground))">{e.label}</text>}</g>;
        })}
        {nodes.map((n: any, i: number) => {
          const color = n.color || "#2563EB";
          const lines = splitLabel(n.label || "", 13);
          const nodeHeight = Math.max(38, 18 + lines.length * 13);
          const nodeWidth = 110;

          return (
            <g key={`n${i}`}>
              <rect x={n.x - nodeWidth / 2} y={n.y - nodeHeight / 2} width={nodeWidth} height={nodeHeight} rx="14" fill={color} opacity="0.96" />
              <SvgMultilineText x={n.x} y={n.y} label={n.label} fill={getContrastTextColor(color)} fontSize={10} maxCharsPerLine={13} />
            </g>
          );
        })}
      </svg>
    </div>
  );
});
DiagramSVG.displayName = "DiagramSVG";

// Map renderer
const MapSVG = forwardRef<HTMLDivElement, { data: any }>(({ data }, ref) => {
  const { regions = [], points = [], arrows = [], legend = [], title = "", width = 400, height = 400 } = data;
  return (
    <div ref={ref} className="my-3 rounded-2xl bg-card border border-border p-3 overflow-x-auto">
      {title && <p className="text-xs font-bold text-foreground mb-2 text-center">{title}</p>}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-sm mx-auto" style={{ maxHeight: 380 }}>
        <defs><marker id="mapArrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill="hsl(var(--muted-foreground))" /></marker></defs>
        {regions.map((r: any, i: number) => {
          const color = r.color || "#3498DB";
          return <g key={`r${i}`}><path d={r.path} fill={withAlpha(color, 0.22)} stroke={color} strokeWidth="2" />{r.label && <SvgMultilineText x={r.labelX || 200} y={r.labelY || 200} label={r.label} fill={getContrastTextColor(color)} fontSize={13} maxCharsPerLine={12} />}</g>;
        })}
        {arrows.map((a: any, i: number) => <g key={`a${i}`}><line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke={a.color || "#2ECC71"} strokeWidth="2" markerEnd="url(#mapArrow)" />{a.label && <text x={(a.x1 + a.x2) / 2 + 10} y={(a.y1 + a.y2) / 2} fontSize="9" fill={a.color || "#2ECC71"}>{a.label}</text>}</g>)}
        {points.map((p: any, i: number) => <g key={`p${i}`}><circle cx={p.x} cy={p.y} r={p.size || 6} fill={p.color || "#E74C3C"} /><text x={p.x + (p.size || 6) + 5} y={p.y + 4} fontSize="11" fontWeight="bold" fill={p.color || "#E74C3C"}>{p.label}</text></g>)}
        {legend.length > 0 && legend.map((l: any, i: number) => <g key={`l${i}`}><rect x={10} y={height - 20 - i * 18} width="10" height="10" rx="2" fill={l.color} /><text x={25} y={height - 12 - i * 18} fontSize="9" fill="hsl(var(--foreground))">{l.label}</text></g>)}
      </svg>
    </div>
  );
});
MapSVG.displayName = "MapSVG";

// Inline SVG renderer (from JSON {type: "svg", content: "..."})
const InlineSVG = forwardRef<HTMLDivElement, { data: any }>(({ data }, ref) => {
  const { content, title = "" } = data;
  const sanitized = React.useMemo(() => {
    if (typeof content !== "string") return "";
    return DOMPurify.sanitize(content, {
      USE_PROFILES: { svg: true, svgFilters: true },
      FORBID_TAGS: ["script", "foreignObject"],
      FORBID_ATTR: ["onload", "onclick", "onerror", "onmouseover", "onmouseenter", "onmouseleave", "onfocus", "onblur"],
    });
  }, [content]);
  return (
    <div ref={ref} className="my-3 rounded-2xl bg-card border border-border p-3 overflow-x-auto">
      {title && <p className="text-xs font-bold text-foreground mb-2 text-center">{title}</p>}
      <div className="w-full max-w-sm mx-auto flex justify-center" dangerouslySetInnerHTML={{ __html: sanitized }} />
    </div>
  );
});
InlineSVG.displayName = "InlineSVG";

// Detect raw biology/visual JSON not wrapped in code fences
const BIOLOGY_KEYS = ["template", "highlight", "notes"];
const VISUAL_JSON_KEYS: Record<string, string[]> = {
  biology: BIOLOGY_KEYS,
};

function looksLikeVisualJson(obj: any): string | null {
  for (const [type, keys] of Object.entries(VISUAL_JSON_KEYS)) {
    if (keys.some((k) => k in obj)) return type;
  }
  return null;
}

// Parse and split visual blocks from content
function parseVisualBlocks(content: string): Array<{ type: "text" | "plot" | "geometry" | "diagram" | "map" | "biology" | "svg"; data: any }> {
  const blocks: Array<{ type: "text" | "plot" | "geometry" | "diagram" | "map" | "biology" | "svg"; data: any }> = [];
  // First pass: fenced code blocks
  const regex = /```(plot|geometry|diagram|map|biology|json)\s*\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) blocks.push({ type: "text", data: content.slice(lastIndex, match.index) });
    try {
      const parsed = JSON.parse(match[2].trim());
      if (match[1] === "json" && parsed.type === "svg" && typeof parsed.content === "string") {
        blocks.push({ type: "svg", data: parsed });
      } else if (match[1] !== "json") {
        blocks.push({ type: match[1] as any, data: parsed });
      } else {
        blocks.push({ type: "text", data: match[0] });
      }
    }
    catch { blocks.push({ type: "text", data: match[0] }); }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) blocks.push({ type: "text", data: content.slice(lastIndex) });

  // Second pass: detect raw JSON visual blocks in text segments
  const result: typeof blocks = [];
  const rawVisualRegex = /(\{[\s\S]*?(?:"template"\s*:\s*"[^"]*"|"type"\s*:\s*"svg")[\s\S]*?\})/g;
  for (const block of blocks) {
    if (block.type !== "text") { result.push(block); continue; }
    const text = block.data as string;
    let textLastIndex = 0;
    let rawMatch;
    rawVisualRegex.lastIndex = 0;
    let found = false;
    while ((rawMatch = rawVisualRegex.exec(text)) !== null) {
      try {
        const parsed = JSON.parse(rawMatch[1]);
        const visualType = looksLikeVisualJson(parsed);
        if (visualType) {
          found = true;
          if (rawMatch.index > textLastIndex) {
            const before = text.slice(textLastIndex, rawMatch.index).trim();
            if (before) result.push({ type: "text", data: before });
          }
          result.push({ type: visualType as any, data: parsed });
          textLastIndex = rawVisualRegex.lastIndex;
        } else if (parsed.type === "svg" && typeof parsed.content === "string") {
          found = true;
          if (rawMatch.index > textLastIndex) {
            const before = text.slice(textLastIndex, rawMatch.index).trim();
            if (before) result.push({ type: "text", data: before });
          }
          result.push({ type: "svg", data: parsed });
          textLastIndex = rawVisualRegex.lastIndex;
        }
      } catch { /* not valid JSON, skip */ }
    }
    if (found && textLastIndex < text.length) {
      const after = text.slice(textLastIndex).trim();
      if (after) result.push({ type: "text", data: after });
    }
    if (!found) result.push(block);
  }
  return result;
}

// LaTeX-aware paragraph
const LatexParagraph = forwardRef<HTMLParagraphElement, { children: React.ReactNode }>(({ children }, forwardedRef) => {
  return <p ref={forwardedRef}>{renderLatexNodes(children)}</p>;
});
LatexParagraph.displayName = "LatexParagraph";

const MarkdownParagraph = forwardRef<HTMLParagraphElement, { children: React.ReactNode }>(({ children }, ref) => {
  return <LatexParagraph ref={ref}>{children}</LatexParagraph>;
});
MarkdownParagraph.displayName = "MarkdownParagraph";

// Main exported component
export function RichMessageRenderer({ content }: { content: string }) {
  const maskedContent = useMemo(() => hideIncompletePhotoMarker(hideIncompleteVisualBlock(maskHttpSources(content))), [content]);
  const blocks = useMemo(() => parseVisualBlocks(maskedContent), [maskedContent]);

  return (
    <div className="space-y-2 text-base leading-8 md:text-[1.05rem]">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "plot": return <PlotSVG key={i} data={block.data} />;
          case "geometry": return <GeometrySVG key={i} data={block.data} />;
          case "diagram": return <DiagramSVG key={i} data={block.data} />;
          case "map": return <MapSVG key={i} data={block.data} />;
          case "svg": return <InlineSVG key={i} data={block.data} />;
          case "biology": return <BiologySVG key={i} data={block.data} />;
          default: {
            const segments = splitOnPhotoMarkers(block.data as string);
            return (
              <div key={i} className="space-y-2">
                {segments.map((seg, j) => {
                  if (seg.kind === "photo") {
                    return (
                      <NotionIllustration
                        key={`p-${i}-${j}`}
                        description={seg.description}
                        subject={seg.subject}
                        topic={seg.topic}
                      />
                    );
                  }
                  return (
                    <div key={`t-${i}-${j}`} className="prose prose-base max-w-none text-foreground dark:prose-invert md:prose-lg prose-p:leading-relaxed prose-li:leading-relaxed prose-headings:text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <ReactMarkdown components={{ p: MarkdownParagraph as any, li: ({ children, ...props }) => <li {...props}>{renderLatexNodes(children)}</li>, a: ({ children }) => <span className="font-semibold text-primary">{children}</span> }}>
                        {seg.data}
                      </ReactMarkdown>
                    </div>
                  );
                })}
              </div>
            );
          }
        }
      })}
    </div>
  );
}

export default RichMessageRenderer;
